import { Button, Card, Cascader, Form, Input, InputNumber, Modal, Select, Space, Table, Typography, Upload } from "antd";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
    DetailHeader as ZhDetailHeader,
    DatePicker as ZhDatePicker,
    Grid as ZhGrid,
    GridItem as ZhGridItem
} from "zhihao-ui-react";

import type {
    DeliveryForm,
    OrderSnapshotVo,
    RecordOrderForm,
    ReceivePaymentInfo,
    ServiceSpecialVo,
    Customer,
    CustomerReceivingAddress,
    File as EntityFile
} from "@zhihaoscm/entity";
import { DELIVERY_STATE, DELIVERY_TYPE, PAYMENT_TYPE_EXCLUDE_SCHEDULE, RECEIVE_STATE } from "@zhihaoscm/meta";

import request, { shippingRequestHttp } from "@/api";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import { AddressAutocomplete, getSuggestionAreaCodes, getSuggestionGeo } from "@/views/components/AddressAutocomplete";
import { AdminTableEmpty } from "@/views/components/TableEmpty";
import "./TradeOrderRecordFormPage.css";

/** 对齐 Vue validatePhone：提货收货手机号允许为空，填写后必须是国内手机号。 */
const PHONE_REGEXP = /^(((13[0-9])|(15[0-9])|(16[0-9])|(17[3-8])|(18[0-9])|(19[0-9])|(14[5-7]))\d{8})$/;
const PAYMENT_FILE_LIMIT = 10;
const PAYMENT_FILE_MAX_SIZE = 50 * 1024 * 1024;
const PAYMENT_FILE_TYPES = [
    "application/x-zip-compressed",
    "application/zip",
    "application/x-rar-compressed",
    "application/x-7z-compressed",
    "application/x-compressed",
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/avif",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/pdf"
];
const PAYMENT_FILE_EXTENSIONS = [
    ".zip",
    ".rar",
    ".7z",
    ".aip",
    ".jpg",
    ".jpeg",
    ".png",
    ".webp",
    ".avif",
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
    ".pdf"
];
const TON_PROOF_MAX_SIZE = 10 * 1024 * 1024;
const TON_PROOF_TYPES = ["image/jpeg", "image/png"];
const TON_PROOF_EXTENSIONS = [".jpg", ".png"];
const tradeOrderRecordEmptyLocale = { emptyText: <AdminTableEmpty /> };

/** 表格空值沿用 Vue el-table 的占位文案，避免空单元格造成列内容错位。 */
const formatTradeOrderTableValue = (value: unknown) =>
    value === undefined || value === null || value === "" ? "--" : String(value);

interface RecordOrderBasicValues {
    /** 商品和订单基础字段，先覆盖 Vue 表单的第一层提交契约。 */
    productId: string;
    orderTime: string;
    lockingTime: string;
    unitPrice?: number;
    ton?: number;
    customerId?: number;
    handlerId?: number;
    mobile: string;
    regionCodeText: string;
    detailAddress: string;
    geo?: { lat: number; lng: number };
}

interface RecordOrderPaymentDraft {
    /** 已存在收款单的编号，仅用于表格展示。 */
    id?: string;
    /** 已存在收款单的单价，仅用于表格展示。 */
    unitPrice?: number;
    /** 已存在收款单的支付时间，仅用于表格展示。 */
    payTime?: string | Date;
    /** 已存在收款单的支付状态，仅用于表格展示。 */
    state?: number;
    /** 收款费用类型。 */
    paymentType: number;
    /** 线下收款金额。 */
    amount: number;
    /** Vue 表单要求晚于下单时间的确认时间。 */
    confirmPayTime: string;
    /** 逗号分隔的付款凭证文件 ID。 */
    paymentFileIds: string;
    /** 费用说明。 */
    remark: string;
    /** 付款说明。 */
    paymentInstruction: string;
    /** 发航/开仓货款关联的提货单。 */
    deliveryId?: string;
    /** 发航货款对应的发航吨位。 */
    ton?: number;
    /** 发航货款对应的吨位证明文件 ID。 */
    tonProveFileId?: number;
}

interface RecordOrderDeliveryDraft {
    /** 快照中的提货单编号，供收款关联选择。 */
    id?: string;
    /** 提货日期。 */
    scheduleDate: string;
    /** 提货结算金额。 */
    settlementAmount: number;
    /** 提货吨数。 */
    estimatedTon: number;
    /** 收货人基础地址字段。 */
    addressInfo: {
        province: string;
        city: string;
        district: string;
        contacts: string;
        cellphone: string;
        detailAddress: string;
        geo?: { lat?: number; lng?: number };
    };
    /** Vue 提货表单保留的关联字段。 */
    transportOrderShipId: string;
    shipId: string;
    /** 已存在提货单的状态，仅用于表格展示。 */
    state?: number;
    /** 已存在提货单的类型，仅用于表格展示。 */
    type?: number;
    /** 已存在提货单的创建时间，仅用于表格展示。 */
    createdTime?: string;
}

interface RecordOrderWaybillCandidate {
    /** Vue 运单选择器返回的运单编号。 */
    id: string;
    /** Vue 状态枚举或后端已格式化的状态文案。 */
    state?: number | string;
    stateLabel?: string;
    goodsType?: string;
    ton?: number;
    shipId?: string;
    shipName?: string;
}

interface RecordOrderShipCandidate {
    /** 船舶 MMSI/业务 ID。 */
    id: string;
    cnname?: string;
    name?: string;
}

interface RecordOrderProductCandidate {
    /** Vue 商品选择器返回的商品 ID。 */
    id: string;
    /** 商品名称，用于组合选择器展示文案。 */
    title?: string;
    /** 商品默认单价，选择商品时回填订单单价。 */
    unitPrice?: number;
    productTypeName?: string;
    productTypeSpec?: string;
    supplierEnterprise?: { name?: string };
}

interface RecordOrderWaybillOption {
    value: string;
    label: string;
    shipId: string;
    shipName: string;
}

interface RecordOrderShipOption {
    value: string;
    label: string;
}

interface TradeOrderAreaOption {
    /** 行政区划编码。 */
    value: string;
    /** 行政区划名称。 */
    label: string;
    /** 下级省、市或区节点。 */
    children?: TradeOrderAreaOption[];
}

export interface TradeOrderRecordFormPageProps {
    /** 沿用 Vue 的订单快照接口，编辑时用于回填基础字段。 */
    loadSnapshot?: (id: string) => Promise<OrderSnapshotVo>;
    /** 沿用 Vue 新增补录订单接口。 */
    createRecordOrder?: (form: RecordOrderForm) => Promise<unknown>;
    /** 沿用 Vue 编辑补录订单接口。 */
    updateRecordOrder?: (id: string, form: RecordOrderForm) => Promise<unknown>;
    /** 沿用 Vue handerInfoPanel 的专员候选接口。 */
    loadHandlers?: (keyword: string) => Promise<ServiceSpecialVo[]>;
    /** 沿用 Vue purchaseUserInfoPanel 的认证用户候选接口。 */
    loadCustomers?: (params: { personalAuth: 1; param: string }) => Promise<Customer[]>;
    /** 沿用 Vue 客户详情接口，读取收货地址。 */
    loadCustomerAddresses?: (id: number) => Promise<CustomerReceivingAddress[]>;
    /** 沿用 Vue 运单选择器接口。 */
    loadWaybills?: (keyword: string) => Promise<RecordOrderWaybillCandidate[]>;
    /** 沿用 Vue 船舶选择器接口。 */
    loadShips?: (keyword: string) => Promise<RecordOrderShipCandidate[]>;
    /** 沿用 Vue goodsInfoPanel 的商品远程选择器接口。 */
    loadProducts?: (keyword: string) => Promise<RecordOrderProductCandidate[]>;
    /** 沿用 Vue areaLib 的省市区数据源。 */
    loadAreaOptions?: () => Promise<TradeOrderAreaOption[]>;
    /** 沿用 Vue fileList 的文件批量回显接口。 */
    loadPaymentFiles?: (ids: number[]) => Promise<EntityFile[]>;
    /** 沿用 Vue fileList 的文件上传接口。 */
    uploadPaymentFile?: (file: Blob) => Promise<EntityFile>;
}

/** 直接复用 Vue 补录订单接口，避免页面依赖无关的服务装饰器。 */
const defaultLoadSnapshot = async (id: string) => {
    const { data } = await request.service.get<OrderSnapshotVo>(`/admin/order-snapshot/vo/${id}`);
    return data;
};
const defaultCreateRecordOrder = (form: RecordOrderForm) =>
    request.service.post("/admin/order/create/record-order", form);
const defaultUpdateRecordOrder = (id: string, form: RecordOrderForm) =>
    request.service.post(`/admin/order/update/record-order/${id}`, form);
const defaultLoadHandlers = async (keyword: string) => {
    const { data } = await request.service.get<ServiceSpecialVo[]>(
        `/admin/product-consignment/find/specials?name=${encodeURIComponent(keyword)}`
    );
    return data;
};
const defaultLoadCustomers = async (params: { personalAuth: 1; param: string }) => {
    const { data } = await request.service.get<Customer[]>("/admin/customer/selector", { params });
    return data;
};
const defaultLoadCustomerAddresses = async (id: number) => {
    const { data } = await request.service.get<{ addresses?: CustomerReceivingAddress[] }>(
        `/admin/customer/find/${id}`
    );
    return data.addresses || [];
};
const defaultLoadWaybills = async (keyword: string) => {
    const { data } = await request.service.get<RecordOrderWaybillCandidate[] | undefined>(
        "/admin/transport/order/ship/selector",
        { params: { keyword } }
    );
    return data || [];
};
const defaultLoadShips = async (keyword: string) => {
    const { data } = await shippingRequestHttp.service.get<
        { list?: RecordOrderShipCandidate[] } | RecordOrderShipCandidate[] | undefined
    >("/ship/selector", { params: { keyword } });
    return Array.isArray(data) ? data : data?.list || [];
};
const defaultLoadProducts = async (keyword: string) => {
    const { data } = await request.service.get<
        { list?: RecordOrderProductCandidate[] } | RecordOrderProductCandidate[] | undefined
    >("/admin/product-consignment/paging-selector", {
        params: { page: 1, size: 10000, keyword, hasShowAll: true }
    });
    return Array.isArray(data) ? data : data?.list || [];
};
const defaultLoadAreaOptions = async (): Promise<TradeOrderAreaOption[]> => {
    const response = await fetch("https://static.zhihaoscm.cn/map/areas.json");
    if (!response.ok) throw new Error("区域数据加载失败");
    const data = (await response.json()) as Array<{
        id: string;
        name: string;
        childs?: Array<{ id: string; name: string; childs?: unknown[] }>;
    }>;
    const mapOptions = (items: typeof data): TradeOrderAreaOption[] =>
        items.map(item => ({
            value: item.id,
            label: item.name,
            children: item.childs ? mapOptions(item.childs as typeof data) : undefined
        }));
    return mapOptions(data);
};
const defaultLoadPaymentFiles = async (ids: number[]) => {
    const { data } = await request.service.post<EntityFile[]>("/dataService/admin/file/ids", ids);
    return data;
};
const defaultUploadPaymentFile = async (file: Blob) => {
    const formData = new FormData();
    formData.append("file", file);
    // 延迟加载文件服务，复用管理后台统一的 FormData.file 上传契约。
    const { fileService } = await import("@/api/services/fileService");
    return fileService.uploadFile(formData);
};

const paymentTypeOptions = [
    {
        value: PAYMENT_TYPE_EXCLUDE_SCHEDULE.ORDER_DEPOSIT.code,
        label: PAYMENT_TYPE_EXCLUDE_SCHEDULE.ORDER_DEPOSIT.intro
    },
    {
        value: PAYMENT_TYPE_EXCLUDE_SCHEDULE.ORDER_PAYMENT.code,
        label: PAYMENT_TYPE_EXCLUDE_SCHEDULE.ORDER_PAYMENT.intro
    },
    {
        value: PAYMENT_TYPE_EXCLUDE_SCHEDULE.SHIPPING_PAYMENT.code,
        label: PAYMENT_TYPE_EXCLUDE_SCHEDULE.SHIPPING_PAYMENT.intro
    },
    {
        value: PAYMENT_TYPE_EXCLUDE_SCHEDULE.WAREHOUSE_OPENING_PAYMENT.code,
        label: PAYMENT_TYPE_EXCLUDE_SCHEDULE.WAREHOUSE_OPENING_PAYMENT.intro
    }
];

/** 将 Vue 补录订单快照中的订单状态和收款类型统一为表单分支使用的数字。 */
function normalizeRecordOrderSnapshot(result: OrderSnapshotVo): OrderSnapshotVo {
    return {
        ...result,
        order: {
            ...result.order,
            state: Number(result.order.state)
        },
        receivePayments: result.receivePayments.map(payment => ({
            ...payment,
            paymentType: Number(payment.paymentType)
        }))
    };
}

/** 补录订单表单，按旧 Vue 的商品、采购用户、收款、提货和其他信息面板组织页面。 */
export function TradeOrderRecordFormPage({
    loadSnapshot = defaultLoadSnapshot,
    createRecordOrder = defaultCreateRecordOrder,
    updateRecordOrder = defaultUpdateRecordOrder,
    loadHandlers = defaultLoadHandlers,
    loadCustomers = defaultLoadCustomers,
    loadCustomerAddresses = defaultLoadCustomerAddresses,
    loadWaybills = defaultLoadWaybills,
    loadShips = defaultLoadShips,
    loadProducts = defaultLoadProducts,
    loadAreaOptions = defaultLoadAreaOptions,
    loadPaymentFiles = defaultLoadPaymentFiles,
    uploadPaymentFile = defaultUploadPaymentFile
}: TradeOrderRecordFormPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const orderId = new URLSearchParams(location.search).get("id");
    const [form] = Form.useForm<RecordOrderBasicValues>();
    const [snapshot, setSnapshot] = useState<OrderSnapshotVo | null>(null);
    const [payments, setPayments] = useState<RecordOrderPaymentDraft[]>([]);
    const [deliveries, setDeliveries] = useState<RecordOrderDeliveryDraft[]>([]);
    // 弹窗索引对应 Vue paymentDialog/deliveryDialog 当前正在编辑的草稿行。
    const [paymentDialogIndex, setPaymentDialogIndex] = useState<number | null>(null);
    const [deliveryDialogIndex, setDeliveryDialogIndex] = useState<number | null>(null);
    const [paymentDialogBackup, setPaymentDialogBackup] = useState<RecordOrderPaymentDraft | null>(null);
    const [deliveryDialogBackup, setDeliveryDialogBackup] = useState<RecordOrderDeliveryDraft | null>(null);
    const [paymentDialogIsNew, setPaymentDialogIsNew] = useState(false);
    const [deliveryDialogIsNew, setDeliveryDialogIsNew] = useState(false);
    const [handlerOptions, setHandlerOptions] = useState<{ value: number; label: string }[]>([]);
    const [customerOptions, setCustomerOptions] = useState<{ value: number; label: string; customer: Customer }[]>([]);
    const [productOptions, setProductOptions] = useState<
        { value: string; label: string; product: RecordOrderProductCandidate }[]
    >([]);
    const [customerAddresses, setCustomerAddresses] = useState<CustomerReceivingAddress[]>([]);
    const [areaOptions, setAreaOptions] = useState<TradeOrderAreaOption[]>([]);
    const [regionCodeSelection, setRegionCodeSelection] = useState<string[]>([]);
    const [paymentFiles, setPaymentFiles] = useState<Record<number, EntityFile[]>>({});
    const [tonProofFiles, setTonProofFiles] = useState<Record<number, EntityFile | undefined>>({});
    const [waybillOptions, setWaybillOptions] = useState<RecordOrderWaybillOption[][]>([]);
    const [shipOptions, setShipOptions] = useState<RecordOrderShipOption[][]>([]);
    const [loading, setLoading] = useState(Boolean(orderId));
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    // 用于避免旧的异步候选请求覆盖用户随后触发的表单校验错误。
    const errorRevisionRef = useRef(0);
    // 凭证元数据失败比并行候选查询失败更具体，避免客户地址请求晚返回时覆盖核心错误提示。
    const errorPriorityRef = useRef(0);
    const showAsyncError = (message: string, priority = 1) => {
        if (priority < errorPriorityRef.current) return;
        errorPriorityRef.current = priority;
        setErrorMessage(message);
    };
    // Vue paymentDialog 在订单未进入有效状态时只开放订单定金和订单货款。
    const availablePaymentTypeOptions = snapshot?.order.state
        ? paymentTypeOptions
        : paymentTypeOptions.filter(option =>
              [
                  PAYMENT_TYPE_EXCLUDE_SCHEDULE.ORDER_DEPOSIT.code,
                  PAYMENT_TYPE_EXCLUDE_SCHEDULE.ORDER_PAYMENT.code
              ].includes(option.value)
          );

    useEffect(() => {
        if (!orderId) {
            setLoading(false);
            return;
        }

        let disposed = false;
        setLoading(true);
        void loadSnapshot(orderId)
            .then(result => {
                if (disposed) return;
                const normalizedResult = normalizeRecordOrderSnapshot(result);
                setSnapshot(normalizedResult);
                setPayments(
                    normalizedResult.receivePayments.map(payment => ({
                        id: payment.id,
                        unitPrice: payment.unitPrice,
                        payTime: payment.payTime,
                        state: payment.state,
                        paymentType: payment.paymentType,
                        amount: payment.amount,
                        confirmPayTime: payment.confirmPayTime || "",
                        paymentFileIds: (payment.paymentFileId || []).join(","),
                        remark: payment.remark || "",
                        paymentInstruction: payment.paymentInstruction || "",
                        deliveryId: payment.deliveryId || "",
                        ton: payment.ton,
                        tonProveFileId: payment.tonProveFileId
                    }))
                );
                setDeliveries(
                    (result.deliveries || []).map(delivery => ({
                        id: delivery.id,
                        scheduleDate: delivery.scheduleDate || "",
                        settlementAmount: delivery.settlementAmount || 0,
                        estimatedTon: delivery.estimatedTon || 0,
                        addressInfo: {
                            province: delivery.addressInfo?.province || "",
                            city: delivery.addressInfo?.city || "",
                            district: delivery.addressInfo?.district || "",
                            contacts: delivery.addressInfo?.contacts || "",
                            cellphone: delivery.addressInfo?.cellphone || "",
                            detailAddress: delivery.addressInfo?.detailAddress || "",
                            geo: delivery.addressInfo?.geo
                        },
                        transportOrderShipId: delivery.transportOrderShipId || "",
                        shipId: delivery.shipId || "",
                        state: delivery.state,
                        type: delivery.type,
                        createdTime: delivery.createdTime
                    }))
                );
                setWaybillOptions(
                    (result.deliveries || []).map(delivery =>
                        delivery.transportOrderShipId
                            ? [
                                  {
                                      value: delivery.transportOrderShipId,
                                      label: delivery.transportOrderShipId,
                                      shipId: delivery.shipId || "",
                                      shipName: delivery.shipId || ""
                                  }
                              ]
                            : []
                    )
                );
                setShipOptions(
                    (result.deliveries || []).map(delivery =>
                        delivery.shipId ? [{ value: delivery.shipId, label: delivery.shipId }] : []
                    )
                );
                // Vue 编辑页通过快照回填子面板；基础字段先在 React 表单中直接回填。
                form.setFieldsValue({
                    productId: result.order.productId,
                    orderTime: result.order.orderTime,
                    lockingTime: result.order.lockingTime,
                    unitPrice: result.order.unitPrice,
                    ton: result.order.ton,
                    customerId: result.order.customerId,
                    handlerId: result.order.handlerId,
                    mobile: result.order.customerEnterprise?.mobile || "",
                    regionCodeText: [
                        result.order.addressInfo?.province,
                        result.order.addressInfo?.city,
                        result.order.addressInfo?.district
                    ]
                        .filter(Boolean)
                        .join("/"),
                    detailAddress: result.order.addressInfo?.detailAddress || "",
                    geo: result.order.addressInfo?.geo as { lat: number; lng: number } | undefined
                });
                setRegionCodeSelection(
                    [
                        result.order.addressInfo?.province,
                        result.order.addressInfo?.city,
                        result.order.addressInfo?.district
                    ].filter(Boolean) as string[]
                );
                const handlerErrorRevision = errorRevisionRef.current;
                void loadHandlers("")
                    .then(result => {
                        if (disposed) return;
                        setHandlerOptions(
                            result.map(item => ({
                                value: item.user.id,
                                label: item.user.employeeId
                                    ? `${item.user.name} | ${item.user.employeeId}`
                                    : item.user.name
                            }))
                        );
                    })
                    .catch(() => {
                        if (!disposed && errorRevisionRef.current === handlerErrorRevision) {
                            // 订单专员候选失败时保留订单表单，并提示远程候选当前不可用。
                            setHandlerOptions([]);
                            showAsyncError("订单专员查询失败");
                        }
                    });
                const customerErrorRevision = errorRevisionRef.current;
                void loadCustomers({
                    personalAuth: 1,
                    param: result.order.customerEnterprise?.realName || ""
                })
                    .then(result => {
                        if (disposed) return;
                        setCustomerOptions(
                            result.map(customer => ({
                                value: customer.id,
                                label: `${customer.code} | ${customer.realName}`,
                                customer
                            }))
                        );
                    })
                    .catch(() => {
                        if (!disposed && errorRevisionRef.current === customerErrorRevision) {
                            // 客户候选查询失败时保留补录表单，并提示用户远程客户数据暂不可用。
                            setCustomerOptions([]);
                            showAsyncError("客户查询失败");
                        }
                    });
                const customerAddressErrorRevision = errorRevisionRef.current;
                void loadCustomerAddresses(result.order.customerId)
                    .then(result => {
                        if (!disposed) setCustomerAddresses(result);
                    })
                    .catch(() => {
                        if (!disposed && errorRevisionRef.current === customerAddressErrorRevision) {
                            // 客户地址失败时保留补录表单，并明确提示用户候选地址暂不可用。
                            setCustomerAddresses([]);
                            showAsyncError("客户地址查询失败");
                        }
                    });
                const productErrorRevision = errorRevisionRef.current;
                void loadProducts(result.order.productId)
                    .then(result => {
                        if (disposed) return;
                        setProductOptions(
                            result.map(product => ({
                                value: product.id,
                                label: `${product.title || product.id} | ${product.id}`,
                                product
                            }))
                        );
                    })
                    .catch(() => {
                        if (!disposed && errorRevisionRef.current === productErrorRevision) {
                            // 商品候选查询失败时保留补录表单，并提示用户远程商品数据暂不可用。
                            setProductOptions([]);
                            showAsyncError("商品查询失败");
                        }
                    });
            })
            .catch(() => {
                if (!disposed) showAsyncError("补录订单快照加载失败", 3);
            })
            .finally(() => {
                if (!disposed) setLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [form, loadCustomerAddresses, loadCustomers, loadHandlers, loadProducts, loadSnapshot, orderId]);

    useEffect(() => {
        let disposed = false;
        const areaErrorRevision = errorRevisionRef.current;
        void loadAreaOptions()
            .then(result => {
                if (!disposed) setAreaOptions(result);
            })
            .catch(() => {
                if (!disposed && errorRevisionRef.current === areaErrorRevision) {
                    // 区域数据是收货地址必需的选择依据，加载失败时沿用 Vue 页面错误反馈并保留表单。
                    setAreaOptions([]);
                    showAsyncError("区域数据加载失败", 2);
                }
            });
        return () => {
            disposed = true;
        };
    }, [loadAreaOptions]);

    useEffect(() => {
        let disposed = false;
        payments.forEach((payment, index) => {
            const paymentFileIds = payment.paymentFileIds
                .split(",")
                .map(fileId => Number(fileId.trim()))
                .filter(fileId => Number.isFinite(fileId) && fileId > 0);
            const ids = [...new Set([...paymentFileIds, payment.tonProveFileId || 0].filter(fileId => fileId > 0))];
            if (!ids.length) return;
            const requestErrorRevision = errorRevisionRef.current;
            void loadPaymentFiles(ids)
                .then(files => {
                    if (!disposed) {
                        setPaymentFiles(current => ({
                            ...current,
                            [index]: files.filter(file => paymentFileIds.includes(file.id))
                        }));
                        if (payment.tonProveFileId) {
                            setTonProofFiles(current => ({
                                ...current,
                                [index]: files.find(file => file.id === payment.tonProveFileId)
                            }));
                        }
                    }
                })
                .catch(() => {
                    if (!disposed && errorRevisionRef.current === requestErrorRevision) {
                        // 文件元数据失败不应中断补录表单，但必须提示用户当前凭证无法回显。
                        setPaymentFiles(current => ({ ...current, [index]: [] }));
                        errorPriorityRef.current = 10;
                        setErrorMessage("付款凭证查询失败");
                    }
                });
        });
        return () => {
            disposed = true;
        };
    }, [loadPaymentFiles, payments]);

    /** 复用 Vue 远程搜索专员候选，保留姓名和工号组合展示。 */
    const searchHandlers = async (keyword: string) => {
        try {
            const result = await loadHandlers(keyword);
            setHandlerOptions(
                result.map(item => ({
                    value: item.user.id,
                    label: item.user.employeeId ? `${item.user.name} | ${item.user.employeeId}` : item.user.name
                }))
            );
        } catch {
            setHandlerOptions([]);
            setErrorMessage("订单专员查询失败");
        }
    };

    /** 复用 Vue 认证用户候选查询，并在选中用户后加载其收货地址。 */
    const searchCustomers = async (keyword: string) => {
        try {
            const result = await loadCustomers({ personalAuth: 1, param: keyword });
            setCustomerOptions(
                result.map(customer => ({
                    value: customer.id,
                    label: `${customer.code} | ${customer.realName}`,
                    customer
                }))
            );
        } catch {
            setCustomerOptions([]);
            setErrorMessage("客户查询失败");
        }
    };

    /** 复用 Vue 商品远程选择器，并按商品名称和 ID 组合展示。 */
    const searchProducts = async (keyword: string) => {
        try {
            const result = await loadProducts(keyword);
            setProductOptions(
                result.map(product => ({
                    value: product.id,
                    label: `${product.title || product.id} | ${product.id}`,
                    product
                }))
            );
        } catch {
            // 用户主动搜索商品失败时清空旧候选，避免继续误选，并反馈接口异常。
            setProductOptions([]);
            setErrorMessage("商品查询失败");
        }
    };

    /** 选择商品时同步 Vue goodsInfoPanel 的默认单价回填行为。 */
    const changeProduct = (productId: string) => {
        const selectedProduct = productOptions.find(option => option.value === productId)?.product;
        if (selectedProduct?.unitPrice !== undefined) form.setFieldValue("unitPrice", selectedProduct.unitPrice);
    };

    const changeCustomer = async (customerId: number) => {
        const selectedCustomer = customerOptions.find(option => option.value === customerId)?.customer;
        form.setFieldValue("mobile", selectedCustomer?.mobile || "");
        try {
            setCustomerAddresses(await loadCustomerAddresses(customerId));
        } catch {
            setCustomerAddresses([]);
        }
    };

    /** 对齐 Vue purchaseUserInfoPanel.selectAddress，将客户地址候选回填到主订单收货地址。 */
    const selectCustomerAddress = (address: CustomerReceivingAddress) => {
        const regionCodes = [address.province, address.city, address.district].filter(Boolean);
        setRegionCodeSelection(regionCodes);
        form.setFieldsValue({
            regionCodeText: regionCodes.join("/"),
            detailAddress: address.detailAddress || ""
        });
    };

    /** 上传付款凭证并同步 Vue paymentFileId 数组，保留当前收款行的文件顺序。 */
    const uploadPaymentFileForRow = async (index: number, file: Blob) => {
        try {
            const uploadedFile = await uploadPaymentFile(file);
            if (!uploadedFile) {
                setErrorMessage("付款凭证上传失败");
                return;
            }
            const nextFiles = [...(paymentFiles[index] || []), uploadedFile];
            setPaymentFiles(current => ({ ...current, [index]: nextFiles }));
            updatePayment(index, { paymentFileIds: nextFiles.map(item => String(item.id)).join(",") });
        } catch {
            setErrorMessage("付款凭证上传失败");
        }
    };

    /** 对齐 Vue SingleUpload，上传发航货款吨位证明并回填文件 ID。 */
    const uploadTonProofFileForRow = async (index: number, file: Blob & { name?: string }) => {
        try {
            const uploadedFile = await uploadPaymentFile(file);
            if (!uploadedFile) {
                setErrorMessage("吨位证明上传失败");
                return;
            }
            setTonProofFiles(current => ({ ...current, [index]: uploadedFile }));
            updatePayment(index, { tonProveFileId: uploadedFile.id });
        } catch {
            setErrorMessage("吨位证明上传失败");
        }
    };

    /** 对齐 Vue SingleUpload 的 JPG/PNG 和 10MB 上传前限制。 */
    const validateTonProofFile = (file: Blob & { name?: string }) => {
        const fileName = file.name?.toLowerCase() || "";
        const supported =
            TON_PROOF_TYPES.includes(file.type) || TON_PROOF_EXTENSIONS.some(extension => fileName.endsWith(extension));
        if (!supported) {
            setErrorMessage("仅支持 jpg,png 文件");
            return false;
        }
        if (file.size > TON_PROOF_MAX_SIZE) {
            setErrorMessage("文件大小超过限制");
            return false;
        }
        return true;
    };

    /** 移除当前收款行的吨位证明并清空 Vue tonProveFileId。 */
    const removeTonProofFile = (index: number) => {
        setTonProofFiles(current => ({ ...current, [index]: undefined }));
        updatePayment(index, { tonProveFileId: undefined });
    };

    /** 对齐 Vue fileList.beforeUpload，上传前拦截数量、大小和文件类型不合法的文件。 */
    const validatePaymentFile = (index: number, file: Blob & { name?: string }) => {
        if ((paymentFiles[index] || []).length >= PAYMENT_FILE_LIMIT) {
            setErrorMessage(`最多仅可上传 ${PAYMENT_FILE_LIMIT} 个文件`);
            return false;
        }
        if (file.size >= PAYMENT_FILE_MAX_SIZE) {
            setErrorMessage("每个文件不可超过 50MB，请重新上传");
            return false;
        }
        const fileName = file.name?.toLowerCase() || "";
        const supported =
            PAYMENT_FILE_TYPES.includes(file.type) ||
            PAYMENT_FILE_EXTENSIONS.some(extension => fileName.endsWith(extension));
        if (!supported) {
            setErrorMessage("存在不支持的文件格式，请重新选择上传");
            return false;
        }
        return true;
    };

    /** 删除当前收款行的付款凭证并同步 ID 文本，不删除服务端历史文件。 */
    const removePaymentFile = (index: number, fileId: number) => {
        const nextFiles = (paymentFiles[index] || []).filter(file => file.id !== fileId);
        setPaymentFiles(current => ({ ...current, [index]: nextFiles }));
        updatePayment(index, { paymentFileIds: nextFiles.map(item => String(item.id)).join(",") });
    };

    /** 选择三级区域后同步旧 Vue 提交字段，保留现有 `/` 分隔编码契约。 */
    const changeRegionCode = (value: (string | number)[]) => {
        const regionCodes = value.map(String);
        setRegionCodeSelection(regionCodes);
        form.setFieldValue("regionCodeText", regionCodes.join("/"));
    };

    /** 新增一条与 Vue paymentDialog 对齐的线下收款草稿。 */
    const addPayment = () => {
        const draft: RecordOrderPaymentDraft = {
            paymentType: PAYMENT_TYPE_EXCLUDE_SCHEDULE.ORDER_DEPOSIT.code,
            amount: 0,
            confirmPayTime: "",
            paymentFileIds: "",
            remark: "",
            paymentInstruction: "",
            deliveryId: "",
            ton: undefined,
            tonProveFileId: undefined
        };
        const index = payments.length;
        setPayments(current => [...current, draft]);
        setPaymentDialogBackup(draft);
        setPaymentDialogIsNew(true);
        setPaymentDialogIndex(index);
    };

    /** 打开已有收款行，保留取消时恢复 Vue 弹窗草稿的能力。 */
    const editPayment = (index: number) => {
        setPaymentDialogBackup(payments[index] || null);
        setPaymentDialogIsNew(false);
        setPaymentDialogIndex(index);
    };

    /** 关闭收款弹窗；取消新增时移除临时行，取消编辑时恢复打开前值。 */
    const closePaymentDialog = (save: boolean) => {
        if (!save && paymentDialogIndex !== null) {
            if (paymentDialogIsNew) removePayment(paymentDialogIndex);
            else if (paymentDialogBackup) {
                setPayments(current =>
                    current.map((payment, index) => (index === paymentDialogIndex ? paymentDialogBackup : payment))
                );
            }
        }
        setPaymentDialogIndex(null);
        setPaymentDialogBackup(null);
    };

    /** 统一更新收款行，保持列表状态可直接映射成 Vue 接口数组。 */
    const updatePayment = (index: number, patch: Partial<RecordOrderPaymentDraft>) => {
        setPayments(current =>
            current.map((payment, paymentIndex) => (paymentIndex === index ? { ...payment, ...patch } : payment))
        );
    };

    const removePayment = (index: number) => {
        setPayments(current => current.filter((_, paymentIndex) => paymentIndex !== index));
    };

    /** 新增一条与 Vue deliveryDialog 对齐的手动提货草稿。 */
    const addDelivery = () => {
        const draft: RecordOrderDeliveryDraft = {
            scheduleDate: "",
            settlementAmount: 0,
            estimatedTon: 0,
            addressInfo: { province: "", city: "", district: "", contacts: "", cellphone: "", detailAddress: "" },
            transportOrderShipId: "",
            shipId: ""
        };
        const index = deliveries.length;
        setDeliveries(current => [...current, draft]);
        setWaybillOptions(current => [...current, []]);
        setShipOptions(current => [...current, []]);
        setDeliveryDialogBackup(draft);
        setDeliveryDialogIsNew(true);
        setDeliveryDialogIndex(index);
    };

    /** 打开已有提货行，编辑内容仍然复用原有地址联想和远程选择逻辑。 */
    const editDelivery = (index: number) => {
        setDeliveryDialogBackup(deliveries[index] || null);
        setDeliveryDialogIsNew(false);
        setDeliveryDialogIndex(index);
    };

    /** 关闭提货弹窗；取消时恢复草稿，避免误把弹窗中的半成品提交到主表单。 */
    const closeDeliveryDialog = (save: boolean) => {
        if (!save && deliveryDialogIndex !== null) {
            if (deliveryDialogIsNew) removeDelivery(deliveryDialogIndex);
            else if (deliveryDialogBackup) {
                setDeliveries(current =>
                    current.map((delivery, index) => (index === deliveryDialogIndex ? deliveryDialogBackup : delivery))
                );
            }
        }
        setDeliveryDialogIndex(null);
        setDeliveryDialogBackup(null);
    };

    /** 统一更新提货行，提交时转换成 Vue DeliveryForm 数组。 */
    const updateDelivery = (index: number, patch: Partial<RecordOrderDeliveryDraft>) => {
        setDeliveries(current =>
            current.map((delivery, deliveryIndex) => (deliveryIndex === index ? { ...delivery, ...patch } : delivery))
        );
    };

    const updateDeliveryAddress = (index: number, patch: Partial<RecordOrderDeliveryDraft["addressInfo"]>) => {
        setDeliveries(current =>
            current.map((delivery, deliveryIndex) =>
                deliveryIndex === index ? { ...delivery, addressInfo: { ...delivery.addressInfo, ...patch } } : delivery
            )
        );
    };

    const removeDelivery = (index: number) => {
        setDeliveries(current => current.filter((_, deliveryIndex) => deliveryIndex !== index));
        setWaybillOptions(current => current.filter((_, optionIndex) => optionIndex !== index));
        setShipOptions(current => current.filter((_, optionIndex) => optionIndex !== index));
    };

    /** 远程查询 Vue 运单候选，并把状态、货物和吨数组合成旧页面文案。 */
    const searchDeliveryWaybills = async (index: number, keyword: string) => {
        if (!keyword) {
            setWaybillOptions(current => current.map((options, optionIndex) => (optionIndex === index ? [] : options)));
            return;
        }
        try {
            const result = await loadWaybills(keyword);
            const options = result.map(item => ({
                value: item.id,
                label: `${item.id} | ${item.stateLabel || item.state || "--"} | ${item.goodsType || "--"} | ${item.ton ?? "--"}`,
                shipId: item.shipId || "",
                shipName: item.shipName || ""
            }));
            setWaybillOptions(current => {
                const next = [...current];
                next[index] = options;
                return next;
            });
        } catch {
            setWaybillOptions(current => current.map((options, optionIndex) => (optionIndex === index ? [] : options)));
            setErrorMessage("运单查询失败");
        }
    };

    /** 远程查询 Vue 船舶候选，保留中文名/MMSI 展示。 */
    const searchDeliveryShips = async (index: number, keyword: string) => {
        if (!keyword) {
            setShipOptions(current => current.map((options, optionIndex) => (optionIndex === index ? [] : options)));
            return;
        }
        try {
            const result = await loadShips(keyword);
            const options = result.map(item => ({
                value: item.id,
                label: `${item.cnname || item.name || item.id} | ${item.id}`
            }));
            setShipOptions(current => {
                const next = [...current];
                next[index] = options;
                return next;
            });
        } catch {
            setShipOptions(current => current.map((options, optionIndex) => (optionIndex === index ? [] : options)));
            setErrorMessage("船舶查询失败");
        }
    };

    /** 关联运单后按 Vue 规则自动带出船舶并锁定船舶选择。 */
    const changeDeliveryWaybill = (index: number, value: string) => {
        const selected = waybillOptions[index]?.find(option => option.value === value);
        updateDelivery(index, { transportOrderShipId: value || "", shipId: selected?.shipId || "" });
        setShipOptions(current => {
            const next = [...current];
            next[index] = selected?.shipId
                ? [{ value: selected.shipId, label: `${selected.shipName || selected.shipId} | ${selected.shipId}` }]
                : [];
            return next;
        });
    };

    /** 按 Vue form.vue 规则组装保存/提交标记，复杂列表暂以空数组保留契约字段。 */
    const handleFinish = async (values: RecordOrderBasicValues, hasCommit: 0 | 1) => {
        // 用户开始新的保存/提交校验后，忽略此前已发出的异步元数据错误回调。
        errorRevisionRef.current += 1;
        errorPriorityRef.current = 0;
        setSubmitting(true);
        setErrorMessage("");
        if (payments.length === 0) {
            setErrorMessage("请新增收款信息");
            setSubmitting(false);
            return;
        }
        if (payments.some(payment => payment.amount <= 0 || payment.amount > 999999999.99)) {
            setErrorMessage("请输入0~1000000000的数字");
            setSubmitting(false);
            return;
        }
        if (
            payments.some(
                payment =>
                    [
                        PAYMENT_TYPE_EXCLUDE_SCHEDULE.SHIPPING_PAYMENT.code,
                        PAYMENT_TYPE_EXCLUDE_SCHEDULE.WAREHOUSE_OPENING_PAYMENT.code
                    ].includes(payment.paymentType) &&
                    payment.ton !== undefined &&
                    (payment.ton <= 0 || payment.ton > 999999.99)
            )
        ) {
            setErrorMessage("请输入0~1000000的数字");
            setSubmitting(false);
            return;
        }
        if (
            payments.some(
                payment =>
                    !payment.confirmPayTime ||
                    payment.paymentFileIds
                        .split(",")
                        .map(fileId => Number(fileId.trim()))
                        .filter(fileId => Number.isFinite(fileId) && fileId > 0).length === 0
            )
        ) {
            setErrorMessage("请完善收款金额、确认支付凭证时间和付款凭证");
            setSubmitting(false);
            return;
        }
        const orderTimestamp = new Date(values.orderTime).getTime();
        if (
            Number.isFinite(orderTimestamp) &&
            payments.some(payment => {
                const confirmPayTimestamp = new Date(payment.confirmPayTime).getTime();
                return Number.isFinite(confirmPayTimestamp) && confirmPayTimestamp <= orderTimestamp;
            })
        ) {
            setErrorMessage("确认支付凭证时间需晚于下单时间");
            setSubmitting(false);
            return;
        }
        if (
            payments.some(
                payment =>
                    [
                        PAYMENT_TYPE_EXCLUDE_SCHEDULE.SHIPPING_PAYMENT.code,
                        PAYMENT_TYPE_EXCLUDE_SCHEDULE.WAREHOUSE_OPENING_PAYMENT.code
                    ].includes(payment.paymentType) &&
                    (!payment.deliveryId ||
                        (payment.paymentType === PAYMENT_TYPE_EXCLUDE_SCHEDULE.SHIPPING_PAYMENT.code &&
                            (!payment.tonProveFileId || payment.tonProveFileId <= 0)))
            )
        ) {
            setErrorMessage("请完善关联提货及发航吨位证明");
            setSubmitting(false);
            return;
        }
        // 对齐 Vue deliveryDialog.vue 的保存前校验，避免无效提货记录进入接口。
        if (
            deliveries.some(
                delivery =>
                    !delivery.scheduleDate ||
                    delivery.settlementAmount <= 0 ||
                    delivery.settlementAmount >= 10000000 ||
                    delivery.estimatedTon <= 0 ||
                    delivery.estimatedTon >= 10000000
            )
        ) {
            setErrorMessage("请完善提货日期、结算金额和提货吨数");
            setSubmitting(false);
            return;
        }
        // 对齐 Vue deliveryDialog.vue：收货手机号不是必填，但填写后必须通过手机号格式校验。
        if (
            deliveries.some(
                delivery =>
                    Boolean(delivery.addressInfo.cellphone) && !PHONE_REGEXP.test(delivery.addressInfo.cellphone)
            )
        ) {
            setErrorMessage("请输入正确的手机号");
            setSubmitting(false);
            return;
        }
        const deliveryPaymentTotals = payments.reduce((totals, payment) => {
            const requiresDelivery = [
                PAYMENT_TYPE_EXCLUDE_SCHEDULE.SHIPPING_PAYMENT.code,
                PAYMENT_TYPE_EXCLUDE_SCHEDULE.WAREHOUSE_OPENING_PAYMENT.code
            ].includes(payment.paymentType);
            if (requiresDelivery && payment.deliveryId) {
                totals.set(payment.deliveryId, (totals.get(payment.deliveryId) || 0) + Number(payment.amount));
            }
            return totals;
        }, new Map<string, number>());
        if (
            Array.from(deliveryPaymentTotals.entries()).some(([deliveryId, amount]) => {
                const delivery = deliveries.find(item => item.id === deliveryId);
                return delivery && amount > delivery.settlementAmount;
            })
        ) {
            setErrorMessage("收款的金额总和不能大于提货单的结算金额");
            setSubmitting(false);
            return;
        }
        const receivePaymentForms = payments.map(payment => {
            const requiresDelivery = [
                PAYMENT_TYPE_EXCLUDE_SCHEDULE.SHIPPING_PAYMENT.code,
                PAYMENT_TYPE_EXCLUDE_SCHEDULE.WAREHOUSE_OPENING_PAYMENT.code
            ].includes(payment.paymentType);
            return {
                orderId: orderId || "",
                paymentType: payment.paymentType,
                amount: Number(payment.amount),
                confirmPayTime: payment.confirmPayTime,
                paymentFileId: payment.paymentFileIds
                    .split(",")
                    .map(fileId => Number(fileId.trim()))
                    .filter(fileId => Number.isFinite(fileId) && fileId > 0),
                remark: payment.remark,
                paymentInstruction: payment.paymentInstruction,
                ...(requiresDelivery
                    ? {
                          deliveryId: payment.deliveryId,
                          ton: payment.ton === undefined ? undefined : Number(payment.ton),
                          ...(payment.paymentType === PAYMENT_TYPE_EXCLUDE_SCHEDULE.SHIPPING_PAYMENT.code
                              ? { tonProveFileId: payment.tonProveFileId }
                              : {})
                      }
                    : {})
            } as ReceivePaymentInfo;
        });
        const deliveryForms = deliveries.map(
            delivery =>
                ({
                    scheduleDate: delivery.scheduleDate,
                    settlementAmount: Number(delivery.settlementAmount),
                    estimatedTon: Number(delivery.estimatedTon),
                    settlementUnitAmount:
                        delivery.estimatedTon > 0
                            ? Number((delivery.settlementAmount / delivery.estimatedTon).toFixed(2))
                            : 0,
                    addressInfo: delivery.addressInfo,
                    transportOrderShipId: delivery.transportOrderShipId,
                    shipId: delivery.shipId,
                    productId: values.productId,
                    orderId: orderId || ""
                }) as DeliveryForm
        );
        const [province = "", city = "", district = ""] = values.regionCodeText.split("/").map(item => item.trim());
        const payload = {
            ...values,
            customerId: Number(values.customerId),
            unitPrice: Number(values.unitPrice),
            ton: Number(values.ton),
            handlerId: Number(values.handlerId),
            addressInfo: {
                province,
                city,
                district,
                detailAddress: values.detailAddress,
                cellphone: values.mobile,
                contacts: "",
                areaCode: "",
                geo: values.geo
            },
            hasCommit,
            receivePaymentForms,
            ...(deliveryForms.length > 0 ? { deliveryForms } : {})
        } as unknown as RecordOrderForm;

        try {
            if (orderId) {
                await updateRecordOrder(orderId, payload);
            } else {
                await createRecordOrder(payload);
            }
            notifyOperationSuccess(hasCommit === 1 ? "提交成功" : "保存成功");
            navigate(-1);
        } catch {
            setErrorMessage(hasCommit === 1 ? "补录订单提交失败" : "补录订单保存失败");
        } finally {
            setSubmitting(false);
        }
    };

    // 订阅商品选择值，保证旧 Vue goodsInfoPanel 的品类、规格和供应商字段随选择即时回填。
    const selectedProductId = Form.useWatch("productId", form);
    const selectedProduct = productOptions.find(option => option.value === selectedProductId)?.product;
    // 地址候选接口返回编码，旧 Vue 页面通过 areaLib 展示中文行政区名称；视觉和交互均沿用该转换。
    const resolveAreaLabel = (code: string) => {
        const findLabel = (options: TradeOrderAreaOption[]): string | undefined => {
            for (const option of options) {
                if (option.value === code) return option.label;
                if (option.children) {
                    const label = findLabel(option.children);
                    if (label) return label;
                }
            }
            return undefined;
        };
        return findLabel(areaOptions) || code;
    };
    const formatCustomerAddressRegion = (address: CustomerReceivingAddress) =>
        [address.province, address.city, address.district].filter(Boolean).map(resolveAreaLabel).join("");

    return (
        <section aria-label="trade-order-record-form-page" className="trade-order-record-form-page">
            <Card className="trade-order-record-form-header">
                <ZhDetailHeader title={orderId ? "编辑补录订单" : "新增补录订单"} />
            </Card>

            {errorMessage ? <div className="trade-order-record-form-error">{errorMessage}</div> : null}

            <Form<RecordOrderBasicValues>
                form={form}
                layout="vertical"
                onFinish={values => void handleFinish(values, 0)}
                initialValues={{
                    productId: "",
                    orderTime: "",
                    lockingTime: "",
                    unitPrice: undefined,
                    ton: undefined,
                    customerId: undefined,
                    handlerId: undefined,
                    mobile: "",
                    regionCodeText: "",
                    detailAddress: ""
                }}
            >
                <Card className="trade-order-record-form-section" title="商品信息" loading={loading}>
                    <div className="trade-order-record-form-grid">
                        <Form.Item
                            label="商品编号及名称"
                            name="productId"
                            rules={[{ required: true, message: "请输入商品编号" }]}
                        >
                            <Select
                                aria-label="商品编号"
                                className="w-full"
                                disabled={Boolean(snapshot?.order.state)}
                                filterOption={false}
                                options={productOptions}
                                placeholder="请选择商品"
                                showSearch
                                onChange={changeProduct}
                                onSearch={keyword => void searchProducts(keyword)}
                            />
                        </Form.Item>
                        <Form.Item
                            label="下单时间"
                            name="orderTime"
                            rules={[{ required: true, message: "请输入下单时间" }]}
                        >
                            <ZhDatePicker
                                aria-label="下单时间"
                                format="YYYY-MM-DD HH:mm"
                                placeholder="请选择下单时间"
                                type="datetime"
                                valueFormat="YYYY-MM-DD HH:mm:ss"
                            />
                        </Form.Item>
                        <Form.Item
                            label="锁货时间"
                            name="lockingTime"
                            rules={[{ required: true, message: "请输入锁货时间" }]}
                        >
                            <ZhDatePicker
                                aria-label="锁货时间"
                                format="YYYY-MM-DD HH:mm"
                                placeholder="请选择锁货时间"
                                type="datetime"
                                valueFormat="YYYY-MM-DD HH:mm:ss"
                            />
                        </Form.Item>
                        <Form.Item label="品类">
                            <Input
                                disabled
                                placeholder="请输入品类"
                                value={selectedProduct?.productTypeName || undefined}
                            />
                        </Form.Item>
                        <Form.Item label="规格">
                            <Input
                                disabled
                                placeholder="请输入规格"
                                value={selectedProduct?.productTypeSpec || undefined}
                            />
                        </Form.Item>
                        <Form.Item label="供应商">
                            <Input
                                disabled
                                placeholder="请输入供应商"
                                value={selectedProduct?.supplierEnterprise?.name || undefined}
                            />
                        </Form.Item>
                        <Form.Item
                            label="商品单价"
                            name="unitPrice"
                            rules={[
                                { required: true, message: "请输入商品单价" },
                                {
                                    validator: (_, value: number) =>
                                        value > 0 && value <= 9999.99
                                            ? Promise.resolve()
                                            : Promise.reject(new Error("大于0，小于等于10000的数，保留2位小数"))
                                }
                            ]}
                        >
                            <InputNumber
                                className="w-full"
                                min={0}
                                max={9999.99}
                                precision={2}
                                placeholder="请输入商品单价"
                                addonAfter="元/吨"
                            />
                        </Form.Item>
                        <Form.Item
                            label="采购数量"
                            name="ton"
                            rules={[
                                { required: true, message: "请输入采购数量" },
                                {
                                    validator: (_, value: number) =>
                                        value > 0 && value <= 9999999
                                            ? Promise.resolve()
                                            : Promise.reject(new Error("大于0，小于等于10000000的数"))
                                }
                            ]}
                        >
                            <InputNumber
                                className="w-full"
                                min={0}
                                max={9999999}
                                precision={2}
                                placeholder="请输入采购数量"
                                addonAfter="吨"
                            />
                        </Form.Item>
                        <Form.Item label="预估金额">
                            <Form.Item
                                noStyle
                                shouldUpdate={(previous, current) =>
                                    previous.unitPrice !== current.unitPrice || previous.ton !== current.ton
                                }
                            >
                                {({ getFieldValue }) => {
                                    const unitPrice = Number(getFieldValue("unitPrice") || 0);
                                    const ton = Number(getFieldValue("ton") || 0);
                                    const amount =
                                        unitPrice > 0 && ton > 0
                                            ? `￥${(unitPrice * ton).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                            : "--";
                                    return <Input disabled placeholder="请输入预估金额" value={amount} />;
                                }}
                            </Form.Item>
                        </Form.Item>
                    </div>
                </Card>

                <Card className="trade-order-record-form-section" title="采购用户信息">
                    <div className="trade-order-record-form-grid trade-order-record-form-grid--customer">
                        <Form.Item
                            label="用户名称"
                            name="customerId"
                            rules={[{ required: true, message: "请选择用户" }]}
                        >
                            <Select
                                aria-label="用户名称"
                                className="w-full"
                                filterOption={false}
                                options={customerOptions}
                                placeholder="请选择用户"
                                showSearch
                                onChange={value => void changeCustomer(value)}
                                onSearch={keyword => void searchCustomers(keyword)}
                            />
                        </Form.Item>
                        <Form.Item label="手机号" name="mobile" rules={[{ required: true, message: "请输入手机号" }]}>
                            <Input placeholder="请输入手机号" disabled />
                        </Form.Item>
                        <Form.Item
                            className="trade-order-record-form-address"
                            label="收货地址"
                            name="regionCodeText"
                            rules={[{ required: true, message: "请选择地址" }]}
                        >
                            <Cascader
                                aria-label="收货区域级联"
                                className="w-full"
                                displayRender={labels => labels.map(String).join(" / ")}
                                options={areaOptions}
                                placeholder="请选择地址"
                                value={regionCodeSelection}
                                onChange={value => changeRegionCode(value as (string | number)[])}
                            />
                        </Form.Item>
                        <Form.Item className="trade-order-record-form-address-detail" name="detailAddress">
                            <AddressAutocomplete
                                aria-label="详细地址"
                                regionCode={regionCodeSelection[2] || ""}
                                onSelectSuggestion={suggestion => {
                                    const areaCodes = getSuggestionAreaCodes(suggestion);
                                    const geo = getSuggestionGeo(suggestion);
                                    // 地址候选可补齐未选择的省市区，并保存真实经纬度。
                                    if (!regionCodeSelection[2] && areaCodes.length) changeRegionCode(areaCodes);
                                    if (geo) form.setFieldValue("geo", { lat: geo.lat, lng: geo.lng });
                                }}
                            />
                        </Form.Item>
                    </div>
                    <input aria-label="收货区域编码" readOnly type="hidden" value={regionCodeSelection.join("/")} />
                    {customerAddresses.length > 0 ? (
                        <div className="trade-order-record-address-card" data-testid="trade-order-record-address-card">
                            {customerAddresses.map(address => (
                                <button
                                    key={address.id}
                                    aria-label={`选择收货地址 ${[address.province, address.city, address.district]
                                        .filter(Boolean)
                                        .join("/")} ${address.detailAddress || ""}`}
                                    className={`trade-order-record-address-item${address.isDefault ? " is-default" : ""}`}
                                    onClick={() => selectCustomerAddress(address)}
                                    type="button"
                                >
                                    <span className="trade-order-record-address-region">
                                        {address.isDefault ? <span className="trade-order-record-address-tag">默认</span> : null}
                                        {formatCustomerAddressRegion(address)}
                                    </span>
                                    <span className="trade-order-record-address-detail">{address.detailAddress || "-"}</span>
                                    <span className="trade-order-record-address-contact">
                                        {address.contacts || "-"} {address.cellphone || "-"}
                                    </span>
                                </button>
                            ))}
                        </div>
                    ) : null}
                </Card>

                <Card
                    className="trade-order-record-form-section trade-order-record-payment-section"
                    title={
                        <span>
                            <span className="trade-order-record-form-required">*</span>收款信息
                        </span>
                    }
                >
                    <div className="trade-order-record-form-section-action">
                        <Button disabled={loading || submitting} onClick={addPayment} type="primary">
                            新增收款
                        </Button>
                    </div>
                    <>
                        <Table
                            className="trade-order-record-form-empty-table trade-order-record-payment-table"
                            dataSource={payments.map((payment, index) => ({
                                ...payment,
                                key: `payment-${index}`,
                                index,
                                paymentTypeLabel:
                                    availablePaymentTypeOptions.find(option => option.value === payment.paymentType)?.label ||
                                    payment.paymentType,
                                deliveryLabel:
                                    deliveries.find(delivery => delivery.id === payment.deliveryId)?.id || "--",
                                stateLabel: RECEIVE_STATE.OFFLINE_PAID.intro
                            }))}
                            locale={payments.length === 0 ? tradeOrderRecordEmptyLocale : undefined}
                            pagination={false}
                            scroll={{ x: 2070 }}
                            columns={[
                                { title: "收款编号", dataIndex: "id", key: "id", width: 180, render: formatTradeOrderTableValue },
                                {
                                    title: "关联提货",
                                    dataIndex: "deliveryLabel",
                                    key: "deliveryId",
                                    width: 190,
                                    render: formatTradeOrderTableValue
                                },
                                {
                                    title: "费用类型",
                                    dataIndex: "paymentTypeLabel",
                                    key: "paymentType",
                                    width: 170,
                                    render: formatTradeOrderTableValue
                                },
                                { title: "费用说明", dataIndex: "remark", key: "remark", width: 200, render: formatTradeOrderTableValue },
                                {
                                    title: "单价(元/吨)",
                                    dataIndex: "unitPrice",
                                    key: "unitPrice",
                                    width: 120,
                                    align: "right",
                                    render: formatTradeOrderTableValue
                                },
                                {
                                    title: "数量(吨)",
                                    dataIndex: "ton",
                                    key: "ton",
                                    width: 120,
                                    align: "right",
                                    render: formatTradeOrderTableValue
                                },
                                {
                                    title: "支付金额(元)",
                                    dataIndex: "amount",
                                    key: "amount",
                                    width: 120,
                                    align: "right",
                                    render: formatTradeOrderTableValue
                                },
                                {
                                    title: "付款说明",
                                    dataIndex: "paymentInstruction",
                                    key: "paymentInstruction",
                                    width: 200,
                                    render: formatTradeOrderTableValue
                                },
                                {
                                    title: "支付时间",
                                    dataIndex: "payTime",
                                    key: "payTime",
                                    width: 154,
                                    render: formatTradeOrderTableValue
                                },
                                {
                                    title: "确认支付凭证时间",
                                    dataIndex: "confirmPayTime",
                                    key: "confirmPayTime",
                                    width: 188,
                                    render: formatTradeOrderTableValue
                                },
                                { title: "支付状态", dataIndex: "stateLabel", key: "state", width: 188, render: formatTradeOrderTableValue },
                                {
                                    title: "操作",
                                    key: "operation",
                                    width: 240,
                                    fixed: "right",
                                    render: (_value, record: { index: number }) => (
                                        <Space>
                                            <Button type="link" onClick={() => editPayment(record.index)}>
                                                修改
                                            </Button>
                                            <Button danger type="link" onClick={() => removePayment(record.index)}>
                                                删除
                                            </Button>
                                            <Button type="link">查看凭证</Button>
                                        </Space>
                                    )
                                }
                            ]}
                        />
                        <Modal
                            centered
                            className="trade-order-record-payment-dialog"
                            destroyOnHidden={false}
                            open={paymentDialogIndex !== null}
                            okText="提交"
                            title={paymentDialogIsNew ? "新增收款" : "修改收款"}
                            width={800}
                            cancelText="取消"
                            onCancel={() => closePaymentDialog(false)}
                            onOk={() => closePaymentDialog(true)}
                        >
                        <Space orientation="vertical" className="w-full" size="middle">
                            {payments.map((payment, index) => paymentDialogIndex === index ? (
                                <Card
                                    key={`payment-${index}`}
                                    size="small"
                                    title={`收款${index + 1}`}
                                    extra={
                                        <Button danger onClick={() => removePayment(index)} type="link">
                                            删除收款{index + 1}
                                        </Button>
                                    }
                                >
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <Typography.Text strong>费用类型</Typography.Text>
                                            <Select
                                                aria-label={`收款${index + 1}费用类型`}
                                                className="mt-2 w-full"
                                                options={availablePaymentTypeOptions}
                                                value={payment.paymentType}
                                                onChange={value => updatePayment(index, { paymentType: value })}
                                            />
                                        </div>
                                        {[
                                            PAYMENT_TYPE_EXCLUDE_SCHEDULE.SHIPPING_PAYMENT.code,
                                            PAYMENT_TYPE_EXCLUDE_SCHEDULE.WAREHOUSE_OPENING_PAYMENT.code
                                        ].includes(payment.paymentType) ? (
                                            <div>
                                                <Typography.Text strong>关联提货</Typography.Text>
                                                <Select
                                                    aria-label={`收款${index + 1}关联提货`}
                                                    className="mt-2 w-full"
                                                    options={deliveries
                                                        .filter(delivery => delivery.id)
                                                        .map(delivery => ({
                                                            value: delivery.id as string,
                                                            label: `${delivery.id} | ${delivery.scheduleDate || "--"}`
                                                        }))}
                                                    value={payment.deliveryId || undefined}
                                                    onChange={value => updatePayment(index, { deliveryId: value })}
                                                />
                                            </div>
                                        ) : null}
                                        <div>
                                            <Typography.Text strong>支付金额</Typography.Text>
                                            <InputNumber
                                                aria-label={`收款${index + 1}支付金额`}
                                                className="mt-2 w-full"
                                                min={0}
                                                max={999999999.99}
                                                precision={2}
                                                placeholder="请输入支付金额"
                                                value={payment.amount}
                                                onChange={value => updatePayment(index, { amount: Number(value || 0) })}
                                            />
                                        </div>
                                        <div>
                                            <Typography.Text strong>确认支付凭证时间</Typography.Text>
                                            <ZhDatePicker
                                                aria-label={`收款${index + 1}确认支付凭证时间`}
                                                className="mt-2"
                                                format="YYYY-MM-DD HH:mm"
                                                placeholder="请选择确认支付凭证时间"
                                                type="datetime"
                                                valueFormat="YYYY-MM-DD HH:mm"
                                                value={payment.confirmPayTime}
                                                onChange={(_value, formattedValue) =>
                                                    updatePayment(index, {
                                                        confirmPayTime:
                                                            typeof formattedValue === "string" ? formattedValue : ""
                                                    })
                                                }
                                            />
                                        </div>
                                        <div>
                                            <Typography.Text strong>付款凭证</Typography.Text>
                                            <Input
                                                aria-label={`收款${index + 1}付款凭证`}
                                                className="mt-2"
                                                placeholder="请输入文件 ID，多个 ID 用逗号分隔"
                                                value={payment.paymentFileIds}
                                                onChange={event =>
                                                    updatePayment(index, { paymentFileIds: event.target.value })
                                                }
                                            />
                                            <Upload
                                                accept=".zip,.rar,.7z,.jpg,.jpeg,.png,.webp,.avif,.doc,.docx,.xls,.xlsx,.pdf"
                                                beforeUpload={file => {
                                                    if (!validatePaymentFile(index, file)) return false;
                                                    void uploadPaymentFileForRow(index, file);
                                                    return false;
                                                }}
                                                className="mt-2"
                                                showUploadList={false}
                                            >
                                                <Button aria-label={`上传收款${index + 1}付款凭证`} type="dashed">
                                                    上传付款凭证
                                                </Button>
                                            </Upload>
                                            {(paymentFiles[index] || []).length > 0 ? (
                                                <Space wrap className="mt-2">
                                                    {(paymentFiles[index] || []).map(file => (
                                                        <Space key={file.id} size="small">
                                                            <Button
                                                                aria-label={`查看收款${index + 1}付款凭证${file.id}`}
                                                                onClick={() =>
                                                                    window.open(
                                                                        file.path,
                                                                        "_blank",
                                                                        "noopener,noreferrer"
                                                                    )
                                                                }
                                                                type="link"
                                                            >
                                                                {file.name || `文件${file.id}`}
                                                            </Button>
                                                            <Button
                                                                aria-label={`删除收款${index + 1}付款凭证${file.id}`}
                                                                onClick={() => removePaymentFile(index, file.id)}
                                                                type="link"
                                                            >
                                                                删除
                                                            </Button>
                                                        </Space>
                                                    ))}
                                                </Space>
                                            ) : null}
                                        </div>
                                        {[
                                            PAYMENT_TYPE_EXCLUDE_SCHEDULE.SHIPPING_PAYMENT.code,
                                            PAYMENT_TYPE_EXCLUDE_SCHEDULE.WAREHOUSE_OPENING_PAYMENT.code
                                        ].includes(payment.paymentType) ? (
                                            <div>
                                                <Typography.Text strong>发航吨位</Typography.Text>
                                                <InputNumber
                                                    aria-label={`收款${index + 1}发航吨位`}
                                                    className="mt-2 w-full"
                                                    min={0}
                                                    max={999999.99}
                                                    precision={2}
                                                    placeholder="请输入发航吨位"
                                                    value={payment.ton}
                                                    onChange={value =>
                                                        updatePayment(index, { ton: Number(value || 0) })
                                                    }
                                                />
                                            </div>
                                        ) : null}
                                        {payment.paymentType === PAYMENT_TYPE_EXCLUDE_SCHEDULE.SHIPPING_PAYMENT.code ? (
                                            <div>
                                                <Typography.Text strong>吨位证明</Typography.Text>
                                                <Upload
                                                    accept=".jpg,.png"
                                                    beforeUpload={file => {
                                                        if (!validateTonProofFile(file)) return false;
                                                        void uploadTonProofFileForRow(index, file);
                                                        return false;
                                                    }}
                                                    className="mt-2"
                                                    showUploadList={false}
                                                >
                                                    <Button aria-label={`上传收款${index + 1}吨位证明`} type="dashed">
                                                        上传吨位证明
                                                    </Button>
                                                </Upload>
                                                {tonProofFiles[index] ? (
                                                    <Space wrap className="mt-2">
                                                        <Button
                                                            aria-label={`查看收款${index + 1}吨位证明${tonProofFiles[index]?.id}`}
                                                            onClick={() =>
                                                                window.open(
                                                                    tonProofFiles[index]?.path,
                                                                    "_blank",
                                                                    "noopener,noreferrer"
                                                                )
                                                            }
                                                            type="link"
                                                        >
                                                            {tonProofFiles[index]?.name ||
                                                                `文件 ${tonProofFiles[index]?.id}`}
                                                        </Button>
                                                        <Button
                                                            aria-label={`删除收款${index + 1}吨位证明${tonProofFiles[index]?.id}`}
                                                            onClick={() => removeTonProofFile(index)}
                                                            type="link"
                                                        >
                                                            删除
                                                        </Button>
                                                    </Space>
                                                ) : null}
                                                <InputNumber
                                                    aria-label={`收款${index + 1}吨位证明`}
                                                    className="mt-2 w-full"
                                                    min={1}
                                                    placeholder="请输入吨位证明文件ID"
                                                    value={payment.tonProveFileId}
                                                    onChange={value =>
                                                        updatePayment(index, {
                                                            tonProveFileId: value === null ? undefined : Number(value)
                                                        })
                                                    }
                                                />
                                            </div>
                                        ) : null}
                                        <div>
                                            <Typography.Text strong>费用说明</Typography.Text>
                                            <Input.TextArea
                                                aria-label={`收款${index + 1}费用说明`}
                                                className="mt-2"
                                                placeholder="请输入费用说明"
                                                autoSize={{ minRows: 4, maxRows: 5 }}
                                                value={payment.remark}
                                                onChange={event => updatePayment(index, { remark: event.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <Typography.Text strong>付款说明</Typography.Text>
                                            <Input.TextArea
                                                aria-label={`收款${index + 1}付款说明`}
                                                className="mt-2"
                                                placeholder="请输入付款说明"
                                                autoSize={{ minRows: 4, maxRows: 5 }}
                                                value={payment.paymentInstruction}
                                                onChange={event =>
                                                    updatePayment(index, { paymentInstruction: event.target.value })
                                                }
                                            />
                                        </div>
                                    </div>
                                </Card>
                            ) : null)}
                        </Space>
                        </Modal>
                    </>
                </Card>

                <Card className="trade-order-record-form-section" title="提货明细">
                    <div className="trade-order-record-form-section-action">
                        <Button disabled={loading || submitting} onClick={addDelivery} type="primary">
                            新增提货
                        </Button>
                    </div>
                    <>
                        <Table
                            className="trade-order-record-form-empty-table trade-order-record-delivery-table"
                            dataSource={deliveries.map((delivery, index) => ({
                                ...delivery,
                                key: `delivery-${index}`,
                                index,
                                stateLabel: DELIVERY_STATE.PICKED_UP.intro,
                                typeLabel: DELIVERY_TYPE.MANUAL_ADDITION.intro,
                                settlementUnitAmount:
                                    delivery.estimatedTon > 0
                                        ? (delivery.settlementAmount / delivery.estimatedTon).toFixed(2)
                                        : "-"
                            }))}
                            locale={deliveries.length === 0 ? tradeOrderRecordEmptyLocale : undefined}
                            pagination={false}
                            scroll={{ x: 1710 }}
                            columns={[
                                { title: "提货编号", dataIndex: "id", key: "id", width: 180, render: formatTradeOrderTableValue },
                                { title: "提货日期", dataIndex: "scheduleDate", key: "scheduleDate", width: 190, render: formatTradeOrderTableValue },
                                {
                                    title: "结算金额",
                                    dataIndex: "settlementAmount",
                                    key: "settlementAmount",
                                    width: 180,
                                    align: "right",
                                    render: formatTradeOrderTableValue
                                },
                                {
                                    title: "提货吨位（吨）",
                                    dataIndex: "estimatedTon",
                                    key: "estimatedTon",
                                    width: 180,
                                    align: "right",
                                    render: formatTradeOrderTableValue
                                },
                                {
                                    title: "结算单价（元/吨）",
                                    dataIndex: "settlementUnitAmount",
                                    key: "settlementUnitAmount",
                                    width: 180,
                                    align: "right",
                                    render: formatTradeOrderTableValue
                                },
                                { title: "状态", dataIndex: "stateLabel", key: "state", width: 180, render: formatTradeOrderTableValue },
                                { title: "类型", dataIndex: "typeLabel", key: "type", width: 180, render: formatTradeOrderTableValue },
                                { title: "创建时间", dataIndex: "createdTime", key: "createdTime", width: 200, render: formatTradeOrderTableValue },
                                {
                                    title: "操作",
                                    key: "operation",
                                    width: 240,
                                    fixed: "right",
                                    render: (_value, record: { index: number }) => (
                                        <Space>
                                            <Button type="link" onClick={() => editDelivery(record.index)}>
                                                修改
                                            </Button>
                                            <Button danger type="link" onClick={() => removeDelivery(record.index)}>
                                                删除
                                            </Button>
                                        </Space>
                                    )
                                }
                            ]}
                        />
                        <Modal
                            centered
                            className="trade-order-record-delivery-dialog"
                            destroyOnHidden={false}
                            open={deliveryDialogIndex !== null}
                            okText="提交"
                            title={deliveryDialogIsNew ? "新增提货" : "修改提货"}
                            width={920}
                            cancelText="取消"
                            onCancel={() => closeDeliveryDialog(false)}
                            onOk={() => closeDeliveryDialog(true)}
                        >
                        <Space orientation="vertical" className="w-full" size="middle">
                            {deliveries.map((delivery, index) => deliveryDialogIndex === index ? (
                                <Card
                                    key={`delivery-${index}`}
                                    size="small"
                                    title={`提货${index + 1}`}
                                    extra={
                                        <Button danger onClick={() => removeDelivery(index)} type="link">
                                            删除提货{index + 1}
                                        </Button>
                                    }
                                >
                                    {/* 对齐 Vue deliveryForm.vue：提货明细使用四列 zhihao 网格，地址占两列。 */}
                                    <div data-area="trade-order-delivery-detail">
                                        <ZhGrid cols={4} gap={[12, 0]}>
                                            <ZhGridItem span={2}>
                                                <Typography.Text strong>提货日期</Typography.Text>
                                                <Input
                                                    aria-label={`提货${index + 1}提货日期`}
                                                    className="mt-2"
                                                    placeholder="YYYY-MM-DD"
                                                    value={delivery.scheduleDate}
                                                    onChange={event =>
                                                        updateDelivery(index, { scheduleDate: event.target.value })
                                                    }
                                                />
                                            </ZhGridItem>
                                            <ZhGridItem span={2}>
                                                <Typography.Text strong>结算金额</Typography.Text>
                                                <InputNumber
                                                    aria-label={`提货${index + 1}结算金额`}
                                                    className="mt-2 w-full"
                                                    min={0}
                                                    placeholder="请输入结算金额"
                                                    value={delivery.settlementAmount}
                                                    onChange={value =>
                                                        updateDelivery(index, { settlementAmount: Number(value || 0) })
                                                    }
                                                />
                                            </ZhGridItem>
                                            <ZhGridItem span={2}>
                                                <Typography.Text strong>提货吨数</Typography.Text>
                                                <InputNumber
                                                    aria-label={`提货${index + 1}提货吨数`}
                                                    className="mt-2 w-full"
                                                    min={0}
                                                    placeholder="请输入提货吨数"
                                                    value={delivery.estimatedTon}
                                                    onChange={value =>
                                                        updateDelivery(index, { estimatedTon: Number(value || 0) })
                                                    }
                                                />
                                            </ZhGridItem>
                                            <ZhGridItem span={2}>
                                                <Typography.Text strong>结算单价</Typography.Text>
                                                <Typography.Paragraph className="mb-0 mt-2">
                                                    {delivery.estimatedTon > 0
                                                        ? `${(delivery.settlementAmount / delivery.estimatedTon).toFixed(2)} 元/吨`
                                                        : "-"}
                                                </Typography.Paragraph>
                                            </ZhGridItem>
                                            <ZhGridItem span={2}>
                                                <Typography.Text strong>关联运单</Typography.Text>
                                                <Select
                                                    aria-label={`提货${index + 1}关联运单`}
                                                    className="mt-2 w-full"
                                                    filterOption={false}
                                                    options={waybillOptions[index] || []}
                                                    showSearch
                                                    value={delivery.transportOrderShipId || undefined}
                                                    onChange={value => changeDeliveryWaybill(index, value)}
                                                    onSearch={keyword => void searchDeliveryWaybills(index, keyword)}
                                                />
                                            </ZhGridItem>
                                            <ZhGridItem span={2}>
                                                <Typography.Text strong>船舶</Typography.Text>
                                                <Select
                                                    aria-label={`提货${index + 1}船舶`}
                                                    className="mt-2 w-full"
                                                    disabled={Boolean(delivery.transportOrderShipId)}
                                                    filterOption={false}
                                                    options={shipOptions[index] || []}
                                                    showSearch
                                                    value={delivery.shipId || undefined}
                                                    onChange={value => updateDelivery(index, { shipId: value || "" })}
                                                    onSearch={keyword => void searchDeliveryShips(index, keyword)}
                                                />
                                            </ZhGridItem>
                                            <ZhGridItem span={2}>
                                                <Typography.Text strong>收货地址</Typography.Text>
                                                <Cascader
                                                    aria-label={`提货${index + 1}收货地址`}
                                                    className="mt-2 w-full"
                                                    displayRender={labels => labels.map(String).join(" / ")}
                                                    options={areaOptions}
                                                    placeholder="请选择省市区"
                                                    value={[
                                                        delivery.addressInfo.province,
                                                        delivery.addressInfo.city,
                                                        delivery.addressInfo.district
                                                    ].filter(Boolean)}
                                                    onChange={value => {
                                                        const [province = "", city = "", district = ""] =
                                                            value.map(String);
                                                        updateDeliveryAddress(index, { province, city, district });
                                                    }}
                                                />
                                            </ZhGridItem>
                                            <ZhGridItem span={2}>
                                                <Typography.Text strong>收货人</Typography.Text>
                                                <Input
                                                    aria-label={`提货${index + 1}收货人`}
                                                    className="mt-2"
                                                    placeholder="请输入收货人"
                                                    value={delivery.addressInfo.contacts}
                                                    onChange={event =>
                                                        updateDeliveryAddress(index, { contacts: event.target.value })
                                                    }
                                                />
                                            </ZhGridItem>
                                            <ZhGridItem span={2}>
                                                <Typography.Text strong>收货人手机号</Typography.Text>
                                                <Input
                                                    aria-label={`提货${index + 1}收货人手机号`}
                                                    className="mt-2"
                                                    placeholder="请输入收货人手机号"
                                                    value={delivery.addressInfo.cellphone}
                                                    onChange={event =>
                                                        updateDeliveryAddress(index, { cellphone: event.target.value })
                                                    }
                                                />
                                            </ZhGridItem>
                                            <ZhGridItem span={2}>
                                                <div
                                                    data-grid-span="2"
                                                    data-testid={`trade-order-delivery-address-span-2-${index}`}
                                                >
                                                    <Typography.Text strong>详细地址</Typography.Text>
                                                    <AddressAutocomplete
                                                        aria-label={`提货${index + 1}详细地址`}
                                                        className="mt-2"
                                                        placeholder="请输入详细地址"
                                                        regionCode={delivery.addressInfo.district}
                                                        value={delivery.addressInfo.detailAddress}
                                                        onChange={detailAddress =>
                                                            updateDeliveryAddress(index, { detailAddress })
                                                        }
                                                        onSelectSuggestion={suggestion => {
                                                            const areaCodes = getSuggestionAreaCodes(suggestion);
                                                            const geo = getSuggestionGeo(suggestion);
                                                            const patch: Partial<
                                                                RecordOrderDeliveryDraft["addressInfo"]
                                                            > = {};
                                                            if (!delivery.addressInfo.district && areaCodes.length) {
                                                                const [province = "", city = "", district = ""] =
                                                                    areaCodes;
                                                                Object.assign(patch, { province, city, district });
                                                            }
                                                            if (geo) patch.geo = geo;
                                                            updateDeliveryAddress(index, patch);
                                                        }}
                                                    />
                                                </div>
                                            </ZhGridItem>
                                        </ZhGrid>
                                    </div>
                                </Card>
                            ) : null)}
                        </Space>
                        </Modal>
                    </>
                </Card>

                <Card
                    className="trade-order-record-form-section trade-order-record-form-handler-section"
                    title="其他信息"
                >
                    <Form.Item
                        className="trade-order-record-form-handler-item"
                        label="订单专员"
                        name="handlerId"
                        rules={[{ required: true, message: "请选择订单专员" }]}
                    >
                        <Select
                            aria-label="订单专员"
                            className="trade-order-record-form-handler"
                            filterOption={false}
                            options={handlerOptions}
                            placeholder="请选择订单专员"
                            showSearch
                            onSearch={keyword => void searchHandlers(keyword)}
                        />
                    </Form.Item>
                </Card>

                <Space className="operation-fixed-btn" size={0}>
                    {!orderId || snapshot?.order.state === 0 ? (
                        <Button htmlType="submit" loading={submitting} type="primary" aria-label="保存补录订单">
                            保存
                        </Button>
                    ) : null}
                    <Button
                        disabled={submitting}
                        onClick={() => void form.validateFields().then(values => handleFinish(values, 1))}
                        type="primary"
                        aria-label="提交补录订单"
                    >
                        提交
                    </Button>
                    <Button disabled={submitting} onClick={() => navigate(-1)}>
                        返回
                    </Button>
                </Space>
            </Form>

        </section>
    );
}

export default TradeOrderRecordFormPage;
